import React from "react";

import { Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";

import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";

import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";

import Appbar from "./components/drawer/Appbar";
import AppDrawer from "./components/drawer/AppDrawer";

import Home from "../home/Home";
import About from "../about/About";

import SignIn from "../auth/SignIn";
import SignUp from "../auth/SignUp";
import LogOut from "../auth/LogOut";

import Examinations from "../database/examinations/Examinations";
import ExaminationCreate from "../database/examinations/ExaminationCreate.jsx";
import ExaminationView from "../database/examinations/ExaminationView";
import ExaminationEdit from "../database/examinations/ExaminationEdit";
import { ExaminationProvider } from "../../helper-functions/context/ExaminationContext.jsx";
import { createPracticeDraft } from "../../helper-functions/shared/examinationDraft.js";
import Patients from "../database/patients/Patients";
import PatientView from "../database/patients/PatientView";
import PatientCompare from "../database/patients/PatientCompare";

import Account from "../management/account/Account";
import Users from "../management/users/Users";

import NoMatch from "../../helper-components/pages/NoMatch";
import Copyright from "../../helper-components/misc/Copyright";
import UnexpectedError from "../../helper-components/pages/UnexpectedError";

import { getPathFromUrl, handleTourGuide, scrollToTop } from "../../helper-functions/shared/pageUtils";
import { formatChangelogMessage, readSeenVersion, rememberSeenVersion, unseenChangelogEntries } from "../../config/constants/changelog";
import { executeFunctionsAfterConnectionEstablished, executeFunctionsAfterLogin, executeFunctionsAfterPageLoad } from "../../helper-functions/api/apiUtils";

import * as Constants from "../../config/constants/Constants.js";

/**
 * Main page, which includes:
 * sidebars
 * toolbar
 * routes to all sub-pages, which will be shown in the center of the page.
 *
 * Add any periodic queries or functions executed after page reload here.
 */
export default function MainPage(props) {
    const { runTourGuide, setRunTourGuide, tourGuideSteps, setTourGuideSteps, tourGuideStepIndex, setTourGuideStepIndex } = props.browser;
    const tourNavigationLock = React.useRef(false);
    const changelogOffered = React.useRef(false);

    const path = getPathFromUrl();

    // useEffect gets automatically executed when page is loaded.
    React.useEffect(() => {
        console.log("Page reloaded.");
        executeFunctionsAfterPageLoad(props);
    }, []);

    React.useEffect(() => {
        if (props.server.isConnectionToServerEstablished) {
            console.log("Connection with server established.");
            executeFunctionsAfterConnectionEstablished(props);
        }
    }, [props.server.isConnectionToServerEstablished]);

    React.useEffect(() => {
        if (!props.user.isLoggedIn) return;

        console.log("User logged in.");
        handleTourGuide(props);
        executeFunctionsAfterLogin(props);
    }, [props.user.isLoggedIn]);

    React.useEffect(() => {
        const version = props.backend.programStatus?.version;
        if (!version || !props.user.isLoggedIn || props.browser.showInitialTourGuide || props.browser.runTourGuide || changelogOffered.current) return;

        const entries = unseenChangelogEntries(readSeenVersion(), version);
        if (entries.length === 0) return;

        changelogOffered.current = true;
        props.showDialog({
            title: "What's new",
            message: formatChangelogMessage(entries),
            actionTitle: "Got it",
            cancelTitle: "Close",
            callback: () => rememberSeenVersion(version)
        });
    }, [props.backend.programStatus?.version, props.user.isLoggedIn, props.browser.showInitialTourGuide, props.browser.runTourGuide]);

    // Scroll to the top of the page when the route changes.
    React.useEffect(() => {
        scrollToTop();
    }, [props.browser.location.pathname]);

    React.useEffect(() => {
        handleTourGuideSteps();
    }, [path]);

    /**
     * Application tour guide.
     */
    const handleJoyrideCallback = (data) => {
        const { action, index, status, type } = data;

        if (tourNavigationLock.current) return;

        // If the tour is finished or skipped, set the state to not run the tour.
        if ([STATUS.FINISHED, STATUS.SKIPPED, STATUS.PAUSED].includes(status) || [ACTIONS.CLOSE].includes(action)) {
            setRunTourGuide(false);
            setTourGuideStepIndex(0);
            if (status === STATUS.FINISHED) {
                props.browser.navigate("/home");
            }
            return;
        }

        // Update state to advance the tour. A step can name the page it belongs on.
        if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
            const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
            const nextPath = tourGuideSteps[nextIndex]?.data?.path;

            if (nextPath && nextPath !== getPathFromUrl()) {
                tourNavigationLock.current = true;
                props.browser.navigate(nextPath);
                window.setTimeout(() => {
                    setTourGuideStepIndex(nextIndex);
                    tourNavigationLock.current = false;
                }, 400);
                return;
            }

            setTourGuideStepIndex(nextIndex);
        }
    };

    /**
     * Set the tour guide steps according to the current path.
     * If the tour is already running, cancel it.
     */
    const handleTourGuideSteps = () => {
        let steps = [];

        Object.values(Constants.TOUR_GUIDE_STEPS).forEach((guide) => {
            if (guide.paths.includes(path)) {
                steps = guide.steps;
            }
        });

        // Update steps if they are different from the current ones.
        if (steps[0]?.content !== tourGuideSteps[0]?.content) {
            if (runTourGuide) {
                setRunTourGuide(false);
            }

            setTourGuideSteps(steps);
            setTourGuideStepIndex(0);
        }
    };

    return (
        // This component includes the toolbar, drawer and other components that remain the same everywhere you are.
        <Box sx={{ display: "flex", minHeight: "100vh" }}>
            <Joyride
                run={runTourGuide}
                steps={tourGuideSteps}
                stepIndex={tourGuideStepIndex}
                styles={{
                    options: {
                        primaryColor: "#1c4199",
                        zIndex: 9999
                    }
                }}
                locale={{ last: "Close tour" }}
                continuous
                showProgress
                // showSkipButton
                spotlightClicks
                scrollToFirstStep
                disableOverlayClose
                disableScrollParentFix //NB! This needs to be here to fix tables stretching the page.
                callback={handleJoyrideCallback}
            />

            {props.browser.showMainPageComponents && (
                <>
                    <Appbar {...props} />
                    <AppDrawer {...props} />
                </>
            )}

            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) => (theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[900]),
                    flexGrow: 1,
                    overflow: "auto"
                }}>
                {props.browser.showMainPageComponents && <Toolbar />}

                <ErrorBoundary
                    FallbackComponent={({ error, resetErrorBoundary }) => {
                        return <UnexpectedError error={error} resetErrorBoundary={resetErrorBoundary} />;
                    }}>
                    {/* These components here are the only things that will change when the user clicks on another tab. */}
                    <Routes>
                        {/* Home */}
                        <Route path="/" element={<Navigate replace to="/home" />} />
                        <Route path="/home" element={<Home {...props} />} />

                        <Route path="/about" element={<About {...props} />} />
                        <Route path="/admin/about" element={<About {...props} />} />

                        {/* Database tables */}
                        <Route path="/database/examinations" element={<Examinations {...props} />} />
                        <Route path="/database/examinations/new" element={<ExaminationCreate {...props} />} />
                        <Route
                            path="/practice"
                            element={
                                <ExaminationProvider persistence="memory" initialDraft={{ ...createPracticeDraft(), patient: null }}>
                                    <ExaminationCreate {...props} mode="practice" />
                                </ExaminationProvider>
                            }
                        />
                        <Route path="/database/examinations/:id/view" element={<ExaminationView {...props} />} />
                        <Route path="/database/examinations/:id/edit" element={<ExaminationEdit {...props} />} />

                        <Route path="/database/patients" element={<Patients {...props} />} />
                        <Route path="/database/patients/:patientId/compare" element={<PatientCompare {...props} />} />
                        <Route path="/database/patients/:patientId" element={<PatientView {...props} />} />

                        {/* Auth */}
                        <Route path="/signin" element={<SignIn {...props} />} />
                        <Route path="/signup" element={<SignUp {...props} />} />
                        <Route path="/logout" element={<LogOut {...props} />} />

                        {/* Admin */}
                        <Route path="/admin/account" element={<Account {...props} />} />
                        <Route path="/admin/users" element={<Users {...props} />} />

                        {/* Misc. */}
                        <Route path="/error" element={<UnexpectedError {...props} />} />
                        <Route path="*" element={<NoMatch />} />
                    </Routes>
                </ErrorBoundary>

                <Copyright />
            </Box>
        </Box>
    );
}
